import type { PayloadOf } from '@gk/contracts'
import { parseDecimal } from '../lib/format.ts'
import type { Bilingual, JobRow, ParamDef } from './plan.ts'

/** What the job form holds while it is being filled: texts as typed, converted to a command payload only when the planner saves. */
export interface JobDraft {
  templateVersionId: string; taskType: string; titleFr: string; titleEn: string; notes: string; date: string; time: string
  group: number; crew: string; areaIds: string[]; params: Record<string, string>
}

const nameOf = (fr: string, en: string): Bilingual => ({ ...(fr.trim() ? { fr: fr.trim() } : {}), ...(en.trim() ? { en: en.trim() } : {}) })

/** The values of a task type's settings as the server wants them: only the keys the task type declares, empty ones left out, numbers as numbers
 *  (a comma decimal is accepted). A value that cannot be read is dropped, never guessed. */
export function paramsFromDraft(defs: readonly ParamDef[], raw: Record<string, string>): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  for (const d of defs) {
    const v = (raw[d.key] ?? '').trim()
    if (!v) continue
    if (d.kind === 'number') { const n = parseDecimal(v); if (n !== null) out[d.key] = n } else out[d.key] = v
  }
  return out
}

export function draftParams(defs: readonly ParamDef[], values: Record<string, string | number>): Record<string, string> {
  return Object.fromEntries(defs.filter((d) => d.key in values).map((d) => [d.key, String(values[d.key])]))
}

export function buildJobCreate(jobId: string, d: JobDraft, defs: readonly ParamDef[]): PayloadOf<'job.create@1'> {
  const title = nameOf(d.titleFr, d.titleEn)
  const crew = d.crew.trim()
  const params = paramsFromDraft(defs, d.params)
  return {
    job_id: jobId,
    ...(d.templateVersionId ? { template_version_id: d.templateVersionId } : { task_type_code: d.taskType }),
    ...(Object.keys(title).length ? { title } : {}),
    ...(d.notes.trim() ? { notes: d.notes.trim() } : {}),
    ...(Object.keys(params).length ? { params } : {}),
    scheduled_local_date: d.date,
    ...(d.time ? { scheduled_start_local: d.time } : {}),
    sequence_group: d.group,
    ...(crew ? { required_crew: Number(crew) } : {}),
    area_ids: d.areaIds,
  }
}

/** M16b (Z's close, docs/m16-requests/d.md 5): a new job may be asked for with an area and a note (`/plan/new?area=&note=`: the bunker walk's corrective job,
 *  an observation's or a fault's "Créer un travail"). The planner still chooses and saves; nothing is created for him. These are the form's rules for it. */
export interface GivenArea { id: string; area_type_code: string; archived_at: string | null }
interface TypeLike { code: string; applicable_area_types: readonly string[]; archived_at: string | null }
/** A task type applies to an area when it names no kind of area or names this one (the AreaPicker's own filter). */
export const typeFits = (ty: TypeLike | undefined, a: GivenArea | undefined): boolean => !!ty && !!a && (ty.applicable_area_types.length === 0 || ty.applicable_area_types.includes(a.area_type_code))
/** The area handed over, when it is a live area of the course (an unknown or archived id is ignored, never sent). */
export const givenArea = <A extends GivenArea>(areas: readonly A[], id: string | undefined): A | undefined => (id ? areas.find((a) => a.id === id && !a.archived_at) : undefined)
/** The task type the form opens on. With an area handed over: the ONE live type that applies to it, else none (release b review, finding 5: the first that fitted
 *  a bunker was "Désherbage des bunkers" for a sand top-up, and one tap planned it; no code may name a real task, so the choice stays the planner's). With no
 *  area: the first live one, as before. */
export function initialTaskType(types: readonly TypeLike[], area: GivenArea | undefined): string {
  const live = types.filter((x) => !x.archived_at)
  if (!area) return live[0]?.code ?? ''
  const fit = live.filter((x) => typeFits(x, area))
  return fit.length === 1 ? fit[0]!.code : ''
}
/** The task types offered, those that apply to the area handed over first (each list in its own order). */
export function typeOptions<T extends TypeLike>(types: readonly T[], area: GivenArea | undefined): T[] {
  const live = types.filter((x) => !x.archived_at)
  return area ? [...live.filter((x) => typeFits(x, area)), ...live.filter((x) => !typeFits(x, area))] : live
}
/** The note handed over, cut to the form's 1000 characters (the textarea's own `maxLength`). */
export const givenNote = (note: string | undefined): string => (note ?? '').slice(0, 1000)
/** Another task type empties the areas (each type has its own kinds of area), but keeps the area handed over when it is still ticked and the new type applies to it. */
export function areasAfterTypeChange(chosen: readonly string[], area: GivenArea | undefined, next: TypeLike | undefined): string[] {
  return area && chosen.includes(area.id) && typeFits(next, area) ? [area.id] : []
}

const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x))
export const timeText = (t: string | null | undefined): string => (t ? t.slice(0, 5) : '')

/** Only what the planner actually changed, so a stale edit of one field never collides with someone else's edit of another. */
export function buildJobUpdate(job: JobRow & { areaIds: string[] }, d: JobDraft, defs: readonly ParamDef[]): PayloadOf<'job.update@1'>['set'] {
  const set: PayloadOf<'job.update@1'>['set'] = {}
  const title = nameOf(d.titleFr, d.titleEn)
  if (Object.keys(title).length && JSON.stringify([title.fr ?? null, title.en ?? null]) !== JSON.stringify([job.title?.fr ?? null, job.title?.en ?? null])) set.title = title
  if (d.notes.trim() !== (job.notes ?? '')) set.notes = d.notes.trim() || null
  const params = paramsFromDraft(defs, d.params)
  if (JSON.stringify(Object.entries(params).sort()) !== JSON.stringify(Object.entries(job.params).sort())) set.params = params
  if (d.time !== timeText(job.scheduled_start_local)) set.scheduled_start_local = d.time || null
  if (d.group !== job.sequence_group) set.sequence_group = d.group
  const crew = d.crew.trim() ? Number(d.crew) : null
  if (crew !== job.required_crew) set.required_crew = crew
  if (!sameSet(d.areaIds, job.areaIds)) set.area_ids = d.areaIds
  return set
}

/** The overrides that could let this person onto this job: for the job itself, or for the type of the chosen machine, valid now. */
export function usableOverrides<T extends { person_id: string; type_id: string; scope_kind: string; scope_ref: string; valid_from: string; valid_until: string }>(
  overrides: readonly T[], personId: string, jobId: string, assetType: string | null | undefined, now = new Date(),
): T[] {
  return overrides.filter((o) => o.person_id === personId && new Date(o.valid_from) <= now && now < new Date(o.valid_until)
    && ((o.scope_kind === 'job' && o.scope_ref === jobId) || (o.scope_kind === 'asset_type' && !!assetType && o.scope_ref === assetType)))
}
